import _slicedToArray from "/near-peer-app/nearpeer-frontend/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/near-peer-app/nearpeer-frontend/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import { __rest } from 'tslib';
import { invariant } from 'hey-listen';
import { isString, defaults, isEasingGenerator, defaultOffset, fillOffset, progress } from '@motionone/utils';
import { resolveOption } from '../utils/stagger.es.js';
import { animateStyle } from '../animate/animate-style.es.js';
import { withControls } from '../animate/utils/controls.es.js';
import { keyframesList } from '../animate/utils/keyframes.es.js';
import { getOptions } from '../animate/utils/options.es.js';
import { resolveElements } from '../utils/resolve-elements.es.js';
import { calcNextTime } from './utils/calc-time.es.js';
import { addKeyframes } from './utils/edit.es.js';
import { compareByTime } from './utils/sort.es.js';
import { Animation } from '@motionone/animation';
function timeline(definition) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var _a;
  var animationDefinitions = createAnimationsFromTimeline(definition, options);
  /**
   * Create and start animations
   */
  var animationFactories = animationDefinitions.map(function (definition) {
    return animateStyle.apply(void 0, _toConsumableArray(definition).concat([Animation]));
  }).filter(Boolean);
  return withControls(animationFactories, options,
  // Get the duration from the first animation definition
  (_a = animationDefinitions[0]) === null || _a === void 0 ? void 0 : _a[3].duration);
}
function createAnimationsFromTimeline(definition) {
  var _a = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var _a$defaultOptions = _a.defaultOptions,
    defaultOptions = _a$defaultOptions === void 0 ? {} : _a$defaultOptions,
    timelineOptions = __rest(_a, ["defaultOptions"]);
  var animationDefinitions = [];
  var elementSequences = new Map();
  var elementCache = {};
  var timeLabels = new Map();
  var prevTime = 0;
  var currentTime = 0;
  var totalDuration = 0;
  /**
   * Build the timeline by mapping over the definition array and converting
   * the definitions into keyframes and offsets with absolute time values.
   * These will later get converted into relative offsets in a second pass.
   */
  for (var i = 0; i < definition.length; i++) {
    var segment = definition[i];
    /**
     * If this is a timeline label, mark it and skip the rest of this iteration.
     */
    if (isString(segment)) {
      timeLabels.set(segment, currentTime);
      continue;
    } else if (!Array.isArray(segment)) {
      timeLabels.set(segment.name, calcNextTime(currentTime, segment.at, prevTime, timeLabels));
      continue;
    }
    var _segment = _slicedToArray(segment, 3),
      elementDefinition = _segment[0],
      keyframes = _segment[1],
      _segment$ = _segment[2],
      options = _segment$ === void 0 ? {} : _segment$;
    /**
     * If a relative or absolute time value has been specified we need to resolve
     * it in relation to the currentTime.
     */
    if (options.at !== undefined) {
      currentTime = calcNextTime(currentTime, options.at, prevTime, timeLabels);
    }
    /**
     * Keep track of the maximum duration in this definition. This will be
     * applied to currentTime once the definition has been parsed.
     */
    var maxDuration = 0;
    /**
     * Find all the elements specified in the definition and parse value
     * keyframes from their timeline definitions.
     */
    var elements = resolveElements(elementDefinition, elementCache);
    var numElements = elements.length;
    for (var elementIndex = 0; elementIndex < numElements; elementIndex++) {
      var element = elements[elementIndex];
      var elementSequence = getElementSequence(element, elementSequences);
      for (var key in keyframes) {
        var valueSequence = getValueSequence(key, elementSequence);
        var valueKeyframes = keyframesList(keyframes[key]);
        var valueOptions = getOptions(options, key);
        var _valueOptions$duratio = valueOptions.duration,
          duration = _valueOptions$duratio === void 0 ? defaultOptions.duration || defaults.duration : _valueOptions$duratio,
          _valueOptions$easing = valueOptions.easing,
          easing = _valueOptions$easing === void 0 ? defaultOptions.easing || defaults.easing : _valueOptions$easing;
        if (isEasingGenerator(easing)) {
          invariant(key === "opacity" || valueKeyframes.length > 1, "spring must be provided 2 keyframes within timeline()");
          var custom = easing.createAnimation(valueKeyframes, key !== "opacity", function () {
            return 0;
          }, key);
          easing = custom.easing;
          valueKeyframes = custom.keyframes || valueKeyframes;
          duration = custom.duration || duration;
        }
        var delay = resolveOption(options.delay, elementIndex, numElements) || 0;
        var startTime = currentTime + delay;
        var targetTime = startTime + duration;
        /**
         *
         */
        var _valueOptions$offset = valueOptions.offset,
          offset = _valueOptions$offset === void 0 ? defaultOffset(valueKeyframes.length) : _valueOptions$offset;
        /**
         * If there's only one offset of 0, fill in a second with length 1
         *
         * TODO: Ensure there's a test that covers this removal
         */
        if (offset.length === 1 && offset[0] === 0) {
          offset[1] = 1;
        }
        /**
         * Fill out if offset if fewer offsets than keyframes
         */
        var remainder = offset.length - valueKeyframes.length;
        remainder > 0 && fillOffset(offset, remainder);
        /**
         * If only one value has been set, ie [1], push a null to the start of
         * the keyframe array. This will let us mark a keyframe at this point
         * that will later be hydrated with the previous value.
         */
        valueKeyframes.length === 1 && valueKeyframes.unshift(null);
        /**
         * Add keyframes, mapping offsets to absolute time.
         */
        addKeyframes(valueSequence, valueKeyframes, easing, offset, startTime, targetTime);
        maxDuration = Math.max(delay + duration, maxDuration);
        totalDuration = Math.max(targetTime, totalDuration);
      }
    }
    prevTime = currentTime;
    currentTime += maxDuration;
  }
  /**
   * For every element and value combination create a new animation.
   */
  elementSequences.forEach(function (valueSequences, element) {
    for (var _key in valueSequences) {
      var _valueSequence = valueSequences[_key];
      /**
       * Arrange all the keyframes in ascending time order.
       */
      _valueSequence.sort(compareByTime);
      var _keyframes = [];
      var valueOffset = [];
      var valueEasing = [];
      /**
       * For each keyframe, translate absolute times into
       * relative offsets based on the total duration of the timeline.
       */
      for (var _i = 0; _i < _valueSequence.length; _i++) {
        var _valueSequence$_i = _valueSequence[_i],
          at = _valueSequence$_i.at,
          value = _valueSequence$_i.value,
          _easing = _valueSequence$_i.easing;
        _keyframes.push(value);
        valueOffset.push(progress(0, totalDuration, at));
        valueEasing.push(_easing || defaults.easing);
      }
      /**
       * If the first keyframe doesn't land on offset: 0
       * provide one by duplicating the initial keyframe. This ensures
       * it snaps to the first keyframe when the animation starts.
       */
      if (valueOffset[0] !== 0) {
        valueOffset.unshift(0);
        _keyframes.unshift(_keyframes[0]);
        valueEasing.unshift("linear");
      }
      /**
       * If the last keyframe doesn't land on offset: 1
       * provide one with a null wildcard value. This will ensure it
       * stays static until the end of the animation.
       */
      if (valueOffset[valueOffset.length - 1] !== 1) {
        valueOffset.push(1);
        _keyframes.push(null);
      }
      animationDefinitions.push([element, _key, _keyframes, Object.assign(Object.assign(Object.assign({}, defaultOptions), {
        duration: totalDuration,
        easing: valueEasing,
        offset: valueOffset
      }), timelineOptions)]);
    }
  });
  return animationDefinitions;
}
function getElementSequence(element, sequences) {
  !sequences.has(element) && sequences.set(element, {});
  return sequences.get(element);
}
function getValueSequence(name, sequences) {
  if (!sequences[name]) sequences[name] = [];
  return sequences[name];
}
export { createAnimationsFromTimeline, timeline };